import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Badge from '../../components/base/Badge';
import { fetchAllCourses, getMyEnrollmentsApi } from '../../api/apiServices';
import { useProgress } from '../../context/ProgressContext';
import { useAuth } from '../../context/useAuth';

const levelColorMap = {
  Beginner: 'teal',
  Intermediate: 'amber',
  Advanced: 'rose',
};

function CourseCard({ course }) {
  const { isAuthenticated } = useAuth();
  const { isEnrolled, getEnrollment, getLessonProgress } = useProgress();

  const courseId = course._id || course.id;

  // Calculate total lessons
  const totalLessons = course.sections?.reduce((total, section) => {
    return total + (section.lectures?.length || 0);
  }, 0) || (course.lessons?.length || 0);

  const enrolled = isAuthenticated && isEnrolled(courseId);
  const enrollmentObj = enrolled ? getEnrollment(courseId) : null;
  const progress = enrollmentObj?.courseCompleted
    ? 100
    : (enrolled ? getLessonProgress(courseId, totalLessons) : 0);

  return (
    <Link 
      to={`/courses/${courseId}`} 
      className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:-translate-y-1 transition-all cursor-pointer block"
    >
      <div className="relative h-48 overflow-hidden">
        <img 
          src={course.thumbnail} 
          alt={course.title} 
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" 
        />
        <div className="absolute top-3 left-3">
          <Badge label={course.level} variant={levelColorMap[course.level] || 'slate'} />
        </div>
        <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-lg flex items-center gap-1">
          <i className="ri-time-line" />{course.duration}
        </div>

        {enrolled && (
          <div className="absolute bottom-0 left-0 right-0 bg-teal-600/95 px-3 py-1.5 flex items-center justify-between shadow-xs">
            <span className="text-white text-xs font-bold flex items-center gap-1">
              <i className="ri-checkbox-circle-fill text-teal-200" />
              {progress === 100 ? 'Completed' : 'Enrolled'}
            </span>
            <span className="text-teal-100 text-xs font-bold">{progress}% complete</span>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          {course.instructorAvatar && (
            <img 
              src={course.instructorAvatar} 
              alt={course.instructor} 
              className="w-5 h-5 rounded-full object-cover" 
            />
          )}
          <span className="text-xs text-slate-400">{course.instructor || 'Unknown'}</span>
          <span className="text-xs text-slate-300">·</span>
          <span className="text-xs text-slate-400">
            {totalLessons} lesson{totalLessons !== 1 ? 's' : ''}
          </span>
        </div>

        <h3 className="text-base font-bold text-slate-900 leading-snug mb-1.5 line-clamp-2 group-hover:text-teal-600 transition-colors">
          {course.title}
        </h3>
        <p className="text-sm text-slate-500 line-clamp-2 mb-3">{course.description}</p>

        <div className="flex items-center gap-1 mb-3.5">
          {[1,2,3,4,5].map(i => (
            <i 
              key={i} 
              className={`ri-star-${i <= Math.floor(course.rating || 4.5) ? 'fill' : 'line'} text-amber-400 text-xs`} 
            />
          ))}
          <span className="text-xs font-semibold text-slate-700 ml-1">
            {course.rating || 4.5}
          </span>
          <span className="text-xs text-slate-400">
            ({(course.studentsEnrolled || 0).toLocaleString()})
          </span>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {(course.tags || []).slice(0, 3).map((tag, i) => (
            <span key={i} className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
              #{tag}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-3.5 border-t border-slate-100">
          <div>
            {!course.isPaid || !course.price || course.price === 0 ? (
              <span className="text-sm font-extrabold text-emerald-600">Free</span>
            ) : course.hasDiscount && course.discount > 0 ? (
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm font-extrabold text-slate-900">
                  ${(course.price * (1 - (course.discount / 100))).toFixed(2)}
                </span>
                <span className="text-[11px] text-slate-400 line-through">
                  ${Number(course.price).toFixed(2)}
                </span>
                <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.2 rounded">
                  {course.discount}% OFF
                </span>
              </div>
            ) : (
              <span className="text-sm font-extrabold text-slate-900">${course.price}</span>
            )}
          </div>

          <span className={`text-xs font-semibold flex items-center gap-1 ${enrolled ? 'text-teal-600 font-bold' : 'text-slate-800 group-hover:text-teal-600'} transition-colors`}>
            {enrolled ? (progress === 100 ? 'Review' : 'Continue') : 'Enroll'} 
            <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function CoursesPage() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [sortBy, setSortBy] = useState('popular');

  const [courses, setCourses] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { isEnrolled, getLessonProgress } = useProgress();

  // Fetch courses from backend
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchAllCourses();
        
        if (response.success && Array.isArray(response.courses)) {
          // Only show published courses
          const publishedCourses = response.courses.filter(course => course.isPublished === true);
          setCourses(publishedCourses);

          // Extract unique categories dynamically
          const uniqueCategories = [...new Set(
            publishedCourses.map(course => course.category).filter(Boolean)
          )];
          setAllCategories(uniqueCategories);
        } else {
          setError(response.message || "Failed to load courses");
        }
      } catch (err) {
        console.error("Failed to fetch courses:", err);
        setError("Failed to load courses. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  const filteredCourses = useMemo(() => {
    let result = [...courses];

    // Search filter
    if (search.trim()) {
      const term = search.toLowerCase().trim();
      result = result.filter(c => 
        c.title?.toLowerCase().includes(term) ||
        c.description?.toLowerCase().includes(term) ||
        c.instructor?.toLowerCase().includes(term)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(c => c.category === selectedCategory);
    }

    // Level filter
    if (selectedLevel !== 'all') {
      result = result.filter(c => c.level === selectedLevel);
    }

    // Sorting
    if (sortBy === 'popular') {
      result.sort((a, b) => (b.studentsEnrolled || 0) - (a.studentsEnrolled || 0));
    } else if (sortBy === 'rating') {
      result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (sortBy === 'price-low') {
      result.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (sortBy === 'price-high') {
      result.sort((a, b) => (b.price || 0) - (a.price || 0));
    }

    return result;
  }, [courses, search, selectedCategory, selectedLevel, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-rose-600 mb-4">⚠️ {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-3 bg-slate-900 text-white rounded-2xl hover:bg-slate-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-16">
        <div className="bg-white border-b border-slate-100 py-10">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-3xl font-bold text-slate-900 mb-1">All Courses</h1>
            <p className="text-slate-500 mb-6">
              Explore {courses.length} published courses from industry experts
            </p>
            
            <div className="relative max-w-xl">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <i className="ri-search-line" />
              </div>
              <input
                type="text"
                placeholder="Search courses, instructors, or topics..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-400"
              />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <aside className="w-full lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-2xl border border-slate-100 p-6 sticky top-20">
                <h3 className="font-semibold text-slate-800 mb-4">Categories</h3>
                <div className="space-y-1">
                  <button 
                    onClick={() => setSelectedCategory('all')}
                    className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-colors ${selectedCategory === 'all' ? 'bg-teal-50 text-teal-700 font-medium' : 'hover:bg-slate-50 text-slate-600'}`}
                  >
                    All Categories
                  </button>
                  {allCategories.map(cat => (
                    <button 
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-colors ${selectedCategory === cat ? 'bg-teal-50 text-teal-700 font-medium' : 'hover:bg-slate-50 text-slate-600'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                <h3 className="font-semibold text-slate-800 mt-8 mb-4">Level</h3>
                <div className="space-y-1">
                  {['all', 'Beginner', 'Intermediate', 'Advanced'].map(level => (
                    <button 
                      key={level}
                      onClick={() => setSelectedLevel(level)}
                      className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-colors ${selectedLevel === level ? 'bg-teal-50 text-teal-700 font-medium' : 'hover:bg-slate-50 text-slate-600'}`}
                    >
                      {level === 'all' ? 'All Levels' : level}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              <div className="flex justify-between items-center mb-6">
                <p className="text-sm text-slate-500">
                  Showing {filteredCourses.length} Course{filteredCourses.length !== 1 ? 's' : ''}
                </p>
                
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-teal-400 bg-white"
                >
                  <option value="popular">Most Popular</option>
                  <option value="rating">Highest Rated</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>

              {filteredCourses.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-book-3-line text-6xl text-slate-300 mb-4" />
                  <p className="text-slate-600 font-medium">No published courses found</p>
                  <p className="text-slate-400 text-sm mt-1">Check back later or try different filters</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredCourses.map(course => (
                    <CourseCard key={course._id} course={course} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}